import { APP_CONFIG } from '@config';
import { useSwitchTeam } from '@modules/Auth/hooks/useAuth';
import { useActivePrinters, usePrintingMethod } from '@modules/configuration/Printers/hooks';
import { useTheme } from '@shared/components/ui/theme-provider';
import { useAuthStore } from '@shared/stores/authStore';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useMenuTeams } from './hooks/useMenuTeams';
import {
  getBackButton,
  getConfigMenuItems,
  getLogoDropdownItems,
  getMainMenuItems,
  getMainMenuTailItems,
  getSidebarFooterItems,
  mergeMainMenuWithTailItems,
} from './menuConfig';
import { BreadcrumbItem, DropdownItem, HeaderAction, MenuItem, NavigationContextType } from './types';
import { useMenuPermissions } from './useMenuPermissions';

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

interface NavigationProviderProps {
  children: React.ReactNode;
}

export function NavigationProvider({ children }: NavigationProviderProps) {
  const { t } = useTranslation('menu');
  const { i18n } = useTranslation();
  const { setTheme, theme } = useTheme();
  const location = useLocation();
  const { user, role, team } = useAuthStore();
  const { filterMenuItems, filterDropdownItems } = useMenuPermissions();

  const isLightTheme = React.useMemo(() => {
    if (theme === 'light') return true;
    if (theme === 'dark') return false;
    return !window.matchMedia('(prefers-color-scheme: dark)').matches;
  }, [theme]);

  const iconColorClass = React.useMemo(() => (isLightTheme ? '!text-text' : '!text-icons'), [isLightTheme]);

  const iconColorStyle = React.useMemo(() => (isLightTheme ? {} : { color: 'var(--wrh-icons)' }), [isLightTheme]);

  const { isPrintNode, isLoading: isLoadingPrintingMethod } = usePrintingMethod();

  const canAccessPrinters = team?.type === 'tenant_3pl';

  const { printers, isLoading: isLoadingPrinters } = useActivePrinters({
    enabled: !isLoadingPrintingMethod && isPrintNode && canAccessPrinters,
  });

  const { teams: menuTeams } = useMenuTeams();

  const { mutate: switchTeamMutate } = useSwitchTeam();

  // 🎯 Memoizar switchTeam para evitar loops infinitos
  const switchTeamRef = useRef(switchTeamMutate);
  useEffect(() => {
    switchTeamRef.current = switchTeamMutate;
  }, [switchTeamMutate]);

  const switchTeam = useCallback((teamId: number) => {
    switchTeamRef.current(teamId);
  }, []);

  // Estado del menú
  // Inicializar basándose en la ruta actual para mantener el estado al recargar
  const [showConfigMenu, setShowConfigMenu] = useState(() => {
    return location.pathname.startsWith('/configuration');
  });
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPrinterId, setSelectedPrinterId] = useState<number | null>(() => {
    const stored = localStorage.getItem('selectedPrinterId');
    return stored ? parseInt(stored, 10) : null;
  });

  const [selectedLanguage, setSelectedLanguage] = useState<'es' | 'en'>(() => {
    const stored = localStorage.getItem('selectedLanguage');
    return (stored as 'es' | 'en') || 'es';
  });

  useEffect(() => {
    i18n.changeLanguage(selectedLanguage);
  }, []);

  // 🎯 Sincronizar showConfigMenu con la ruta actual
  useEffect(() => {
    const isConfigPath = location.pathname.startsWith('/configuration');
    setShowConfigMenu(isConfigPath);
  }, [location.pathname]);

  // 🎯 Estado para breadcrumbs y acciones (simple, sin cleanup automático)
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [headerActions, setHeaderActions] = useState<HeaderAction[]>([]);

  const [logoDropdownItems, setLogoDropdownItems] = useState<DropdownItem[]>([]);

  const translateMenuItems = (items: MenuItem[]): MenuItem[] =>
    items.map((item) => ({
      ...item,
      label: typeof item.label === 'string' ? t(item.label.replace(/^menu\./, '')) : item.label,
      children: item.children ? translateMenuItems(item.children) : undefined,
    }));

  const toggleConfigMenu = useCallback(() => setShowConfigMenu((prev) => !prev), []);

  const handlePrinterSelect = useCallback((printerId: number | null) => {
    setSelectedPrinterId(printerId);
    localStorage.setItem('selectedPrinterId', String(printerId ?? ''));
    window.dispatchEvent(new CustomEvent('printerSelectionChanged'));
  }, []);

  const handleThemeChange = useCallback(
    (theme: 'light' | 'dark' | 'system') => {
      setTheme(theme);
    },
    [setTheme],
  );

  const handleLanguageChange = useCallback(
    (language: 'es' | 'en') => {
      setSelectedLanguage(language);
      localStorage.setItem('selectedLanguage', language);
      i18n.changeLanguage(language);
    },
    [i18n],
  );

  const menuItems: MenuItem[] = React.useMemo(() => {
    if (showConfigMenu) {
      const backButton = getBackButton(() => setShowConfigMenu(false), t);
      const configItems = translateMenuItems(getConfigMenuItems());
      const filteredConfigItems = filterMenuItems(configItems);
      return [backButton, ...filteredConfigItems];
    }
    const mainItems = translateMenuItems(getMainMenuItems(team?.type));
    const tailItems = translateMenuItems(getMainMenuTailItems());
    const filteredMainItems = filterMenuItems(mainItems);
    const filteredTailItems = filterMenuItems(tailItems);
    return mergeMainMenuWithTailItems(filteredMainItems, filteredTailItems);
  }, [showConfigMenu, t, filterMenuItems, team?.type, toggleConfigMenu]);

  const sidebarFooterItems: MenuItem[] = React.useMemo(() => {
    if (showConfigMenu) return [];
    const footerItems = translateMenuItems(getSidebarFooterItems(toggleConfigMenu));
    return filterMenuItems(footerItems);
  }, [showConfigMenu, t, filterMenuItems, toggleConfigMenu]);

  const pageTitle = React.useMemo(() => {
    const isConfigPath = location.pathname.startsWith('/configuration');
    const sourceItems = isConfigPath ? getConfigMenuItems() : getMainMenuItems(team?.type);

    const flattenMenuItems = (items: MenuItem[]): MenuItem[] => {
      const result: MenuItem[] = [];
      for (const item of items) {
        if (item.path && item.path !== '#') {
          result.push(item);
        }
        if (item.children) {
          result.push(...flattenMenuItems(item.children));
        }
      }
      return result;
    };

    const flatItems: MenuItem[] = flattenMenuItems(sourceItems);

    const matches = flatItems.filter((it) => {
      if (!it.path || it.path === '#') return false;
      if (it.path === location.pathname) return true;
      return location.pathname.startsWith(it.path);
    });

    const match = matches.sort((a, b) => {
      if (!a.path || !b.path) return 0;
      return b.path.length - a.path.length;
    })[0];

    if (match) {
      if (typeof match.title === 'string') {
        const raw = match.title;
        const out = raw.startsWith('menu.') ? t(raw.replace(/^menu\./, '')) : raw;
        return out;
      }
      if (typeof match.label === 'string') {
        const key = match.label.replace(/^menu\./, '');
        const out = t(key);
        return out;
      }
      return typeof match.label === 'string' ? match.label : 'Página';
    }

    if (breadcrumbs.length > 0) {
      const lastBreadcrumb = breadcrumbs[breadcrumbs.length - 1];
      // Si tiene title explícito, usarlo
      if (lastBreadcrumb.title) {
        return lastBreadcrumb.title;
      }
      // Si el label es string, usarlo
      if (typeof lastBreadcrumb.label === 'string') {
        return lastBreadcrumb.label;
      }
      // Fallback a "Página"
      return 'Página';
    }

    return t('inicio');
  }, [location.pathname, t, breadcrumbs, team?.type]);

  useEffect(() => {
    document.title = `${pageTitle} - ${APP_CONFIG.appName}`;
  }, [pageTitle]);

  // Efecto para cargar items del dropdown del logo
  useEffect(() => {
    if (isLoadingPrintingMethod) return;

    try {
      const items = getLogoDropdownItems(
        selectedPrinterId,
        handlePrinterSelect,
        handleThemeChange,
        handleLanguageChange,
        t,
        printers,
        isPrintNode,
        isLoadingPrinters,
        menuTeams,
        switchTeam,
        team?.id ?? null,
        iconColorClass,
        iconColorStyle,
      );

      const translatedItems = items.map((item) =>
        item.type === 'separator'
          ? item
          : {
              ...item,
              label:
                typeof item.label === 'string' && item.label.startsWith('menu.')
                  ? t(item.label.replace(/^menu\./, ''))
                  : item.label,
            },
      );

      const filteredItems = filterDropdownItems(translatedItems);
      setLogoDropdownItems(filteredItems);
    } catch {
      setLogoDropdownItems([]);
    }
  }, [
    selectedPrinterId,
    selectedLanguage,
    t,
    printers,
    isLoadingPrinters,
    menuTeams,
    isLoadingPrintingMethod,
    isPrintNode,
    user?.id,
    role?.id,
    team?.id,
    theme,
    iconColorClass,
    iconColorStyle,
    handlePrinterSelect,
    handleThemeChange,
    handleLanguageChange,
    switchTeam,
    filterDropdownItems,
  ]);

  const value: NavigationContextType = {
    showConfigMenu,
    toggleConfigMenu,
    menuItems,
    sidebarFooterItems,
    helpCenterItem: null, // Ya está incluido en menuItems
    logoDropdownItems,
    pageTitle,
    breadcrumbs,
    setBreadcrumbs,
    headerActions,
    setHeaderActions,
    isOpen,
    setIsOpen,
    selectedPrinterId,
    setSelectedPrinterId: handlePrinterSelect,
  };

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}

export function useNavigationContext() {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigationContext must be used within a NavigationProvider');
  }
  return context;
}
